/**
 * DatasetRecord — spec §F8 dataset detail modal (mockup `openDatasetModal`).
 *
 * `access` is never recomputed here from `role`: the §D4 `/datasets/{id}` row already
 * carries the server's own `access` (`full|limited|none`) for the caller's role, so the
 * download control renders straight off that field — see the task brief and
 * `apps.datasets.catalogue`'s own docstring on why the client never re-derives it.
 *
 * Keyboard/focus behaviour mirrors `SignInGate`'s trap (`features/landing/SignInGate.tsx`
 * ~L385-420): Tab wraps inside the dialog, Escape closes it, and focus returns to whatever
 * had it (the clicked card) once the modal unmounts.
 */
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { datasetDownloadUrl, fetchDataset, queryKeys, type DatasetDomainFacet } from '../../lib/api';
import { EMDASH } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { toast } from '../../lib/toast';
import { DatasetRows } from './DatasetRows';
import { useDatasetPreview } from './preview';

export interface DatasetRecordProps {
  id: string;
  onClose: () => void;
  /** The card grid's own `id → facet` lookup (`facets.domain`, §D4) — the translated
   * domain name shown on the card, reused here so the record's domain row and the card
   * it was opened from never disagree. Falls back to the raw id when the domain is
   * absent from the facets (e.g. an indicator-only domain with no catalogued dataset). */
  domainNames?: Map<string, DatasetDomainFacet>;
}

/** Everything inside the dialog a Tab can reach, in document order (SignInGate's list). */
const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function isoDate(value: string | null): string {
  return value ? value.slice(0, 10) : EMDASH;
}

export function DatasetRecord({ id, onClose, domainNames }: DatasetRecordProps) {
  const { t } = useTranslation();
  const preview = useDatasetPreview();
  /**
   * §2: About | Data. The record always opens on About — the metadata is what the reader
   * clicked *Details* for — and the Data tab only exists at all for a dataset whose rows
   * this reader may read (`showData` below).
   */
  const [tab, setTab] = useState<'about' | 'data'>('about');

  /**
   * Left/Right move between the two tabs (WAI-ARIA's tabs pattern), and the strip is a
   * **roving tabindex**: only the selected tab is a Tab stop, so Tab steps past the strip
   * into the panel rather than making the reader step over every tab first. The arrow
   * selects as it moves — there are two tabs and both are cheap, so there is nothing to be
   * gained by making the reader press Enter as well.
   */
  const onTabKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const next = tab === 'about' ? 'data' : 'about';
    setTab(next);
    e.currentTarget
      .querySelector<HTMLElement>(`[data-testid="record-tab-${next}"]`)
      ?.focus();
  };
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const { data: record, isError } = useQuery({
    queryKey: queryKeys.dataset(id),
    queryFn: fetchDataset,
  });

  // Focus the dialog once its DOM exists; restore focus to whatever opened it once
  // the dialog actually unmounts — the same shape as SignInGate's trap, minus
  // `inert`-ing the page behind: this modal sits over a page that stays
  // interactive-looking through the dimmed `.modal-back`, and §F8 has no requirement
  // to freeze it, only to keep Tab inside the dialog.
  //
  // **The component returns `null` until the record has loaded** (below), so on a
  // real first open — nothing pre-seeded in the query cache — `overlayRef.current`
  // does not exist on the render a naive `useEffect(..., [])` fires for: the focus
  // call is a no-op, the deps never change, and the effect never runs again. Focus
  // is then left on the card that opened the modal, which sits *outside*
  // `.modal-back`'s subtree — so the Escape/Tab handling below, wired as `onKeyDown`
  // on that same subtree, never even sees the keystroke.
  //
  // Fixing that by keying the effect to `record != null` alone re-opens a second
  // problem: `queryKeys.dataset(id, lang)` bakes the interface language into the
  // cache key, and the client keeps no data under the *other* language's key. So
  // switching EN↔FR while the dialog is open makes `record` go defined → undefined
  // (dialog unmounts, restoring focus to the opener) → defined again once the
  // refetch resolves (dialog remounts, stealing focus back in) — a visible focus
  // jump the reader never asked for, on a keystroke that had nothing to do with
  // this dialog.
  //
  // **`focusedRef` is a one-shot latch**, not a dependency: it makes the "move focus
  // in" effect fire on the *first* render where the dialog exists and then never
  // again for the life of this component instance, however many times `record`
  // cycles through undefined afterwards (a language switch, a background refetch).
  // Restoring focus is symmetric — captured once, in the same guarded block, and
  // returned in a **separate mount-only effect** whose cleanup fires exactly once,
  // on the real unmount `onClose` causes, not on every interim `record`-goes-away
  // render.
  const openerRef = useRef<HTMLElement | null>(null);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (record == null || focusedRef.current) return;
    focusedRef.current = true;
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    overlayRef.current?.querySelector<HTMLElement>(TABBABLE)?.focus();
  }, [record]);

  useEffect(() => {
    return () => {
      if (openerRef.current?.isConnected) openerRef.current.focus();
    };
  }, []);

  // Escape is a **document-level** listener, not part of `trapTab` below, and lives for
  // the whole mount rather than only while `record` is defined: the not-stealing-focus
  // fix above means focus can legitimately sit outside `.modal-back` for a moment (the
  // dialog unmounts mid-refetch, focus reverts to `<body>`, the latch stops it moving
  // back in) — an `onKeyDown` scoped to that subtree would miss Escape entirely for as
  // long as focus stays there, even though the dialog reader thinks they're looking at
  // is still open underneath. Binding on `document` means Escape closes it regardless
  // of where focus happens to be at that instant.
  useEffect(() => {
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // The Data tab's rows search (`DatasetRows.tsx`) is a native `type="search"` box, whose
      // own Escape clears its text — an expectation every other search box on the page
      // shares. Without this guard the dialog-level handler above wins the race and closes
      // the whole record out from under a reader who only meant to clear three letters.
      const target = e.target;
      if (target instanceof HTMLElement && target.closest('[data-testid="record-rows-search"]')) {
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  /**
   * A record the server will not answer for — a 403 on a restricted dataset, a 404 on one
   * that has been withdrawn, or a dead link into the catalogue.
   *
   * Without this the dialog simply never appears: the render below returns `null` until
   * `record` is defined, and on an error it never becomes defined, so the reader who clicked
   * *View metadata →* on a gated layer row gets no modal, no message and no way to tell
   * whether the click registered. Saying so and standing down is the honest answer — the
   * toast names the reason and `onClose` clears the store's `recordId`, so a second click
   * on the same row asks again rather than being swallowed by an id that is already set.
   *
   * `closedRef` is a one-shot latch, like `focusedRef` above: `onClose` unmounts this
   * component in the normal case, but this effect must not be able to fire twice if a
   * re-render beats the unmount, and it must not fight a caller that keeps the dialog
   * mounted. The **loading** state is untouched — `isError` is false while the query is
   * pending, so a slow first open still renders nothing and waits, exactly as before.
   *
   * The app's client never retries a 4xx (`createQueryClient`'s own `retry` predicate), so
   * this is reached on the first failed response rather than after a backoff.
   *
   * `record != null` is part of the guard, not decoration: every `useQuery` fires a
   * background refetch on mount, and a *refetch* that fails while the dialog is already
   * showing a perfectly good record must not tear it out from under the reader. Only the
   * case with nothing to render — the first load, or the load of the other language's key
   * after an EN↔FR switch — closes.
   */
  const closedRef = useRef(false);
  useEffect(() => {
    if (!isError || record != null || closedRef.current) return;
    closedRef.current = true;
    toast(t('catalogue.record.unavailable'));
    onClose();
  }, [isError, record, onClose, t]);

  const trapTab = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const stops = Array.from(overlayRef.current?.querySelectorAll<HTMLElement>(TABBABLE) ?? []);
    const first = stops[0];
    const last = stops[stops.length - 1];
    if (!first || !last) return;
    if (document.activeElement !== (e.shiftKey ? first : last)) return;
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  }, []);

  /**
   * "Preview on map" — the card's handler, verbatim (client item 9). The modal's only extra
   * is closing itself; where the preview lands is `preview.ts`'s single answer, so the
   * record and the card behind it can never open two different layers.
   */
  const handlePreview = () => {
    if (!record) return;
    // A record with nowhere to go stays **open** under its toast: closing the modal as well
    // would read as a click that worked. Everything else is the card's handler exactly.
    if (!record.preview) {
      toast(t('catalogue.record.tabularPreview'));
      return;
    }
    onClose();
    preview(record);
  };

  if (!record) return null;

  const rows: [string, string][] = [
    [t('catalogue.record.title'), record.title],
    [t('catalogue.record.abstract'), record.abstract],
    [
      t('catalogue.record.domain'),
      record.domain ? (domainNames?.get(record.domain)?.name ?? record.domain) : EMDASH,
    ],
    [t('catalogue.record.source'), record.source_org],
    [t('catalogue.record.format'), record.format],
    [t('catalogue.record.crs'), record.crs],
    [t('catalogue.record.coverage'), record.coverage ?? EMDASH],
    [
      t('catalogue.record.classification'),
      record.is_provisional
        ? `${t(`catalogue.cls.${record.classification}`)} · ${t('catalogue.provisionalTitle')}`
        : t(`catalogue.cls.${record.classification}`),
    ],
    [t('catalogue.record.cadence'), record.cadence ?? EMDASH],
    [t('catalogue.record.licence'), record.licence ?? EMDASH],
    [t('catalogue.record.lineage'), record.lineage ?? EMDASH],
    [t('catalogue.record.contact'), record.contact ?? EMDASH],
  ];

  /*
    The provenance four (client, 2026-09-22), appended rather than woven in and only where
    the record carries them.

    Every row above prints an em dash when it is empty, because those are the fields the
    catalogue has always asked for and a blank one is a gap worth seeing. These four are new:
    nothing published before today has them, so printing four dashes on every existing record
    would say the catalogue is four-fifths empty when it is simply older than the question.
  */
  for (const [key, value] of [
    ['sourceDetail', record.source_detail],
    ['captureMethod', record.capture_method],
    ['accuracy', record.accuracy],
    ['resolution', record.resolution],
  ] as const) {
    if (value) rows.push([t(`catalogue.record.${key}`), value]);
  }

  const canDownload = record.access === 'full';
  /**
   * §2: rows exist and this role may read them. `access === 'none'` would 403 on the rows
   * endpoint, and a tab that can only fail is worse than no tab — that reader gets the
   * About tab and the existing locked-download note, as the spec says.
   */
  const showData = record.has_rows && record.access !== 'none';

  return (
    <div className="modal-back show" onKeyDown={trapTab}>
      <div
        // The Data tab needs the width: a provider's ten columns do not fit the 720px the
        // metadata table was sized for, and the alternative is a horizontal scrollbar on a
        // desktop screen with room to spare.
        className={showData && tab === 'data' ? 'modal rec-wide' : 'modal'}
        ref={overlayRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dsRecordTitle"
        data-testid="dataset-record"
      >
        <header>
          <div>
            <h3 id="dsRecordTitle">{record.title}</h3>
          </div>
          <button
            type="button"
            className="close"
            aria-label={t('catalogue.record.close')}
            onClick={onClose}
          >
            <Icon name="x" />
          </button>
        </header>
        {showData ? (
          <div
            className="rec-tabs"
            role="tablist"
            aria-label={record.title}
            onKeyDown={onTabKeyDown}
          >
            <button
              type="button"
              role="tab"
              id="recTabAbout"
              className={tab === 'about' ? 'rec-tab on' : 'rec-tab'}
              aria-selected={tab === 'about'}
              aria-controls="recPanel"
              tabIndex={tab === 'about' ? 0 : -1}
              data-testid="record-tab-about"
              onClick={() => setTab('about')}
            >
              {t('catalogue.record.tabAbout')}
            </button>
            <button
              type="button"
              role="tab"
              id="recTabData"
              className={tab === 'data' ? 'rec-tab on' : 'rec-tab'}
              aria-selected={tab === 'data'}
              aria-controls="recPanel"
              tabIndex={tab === 'data' ? 0 : -1}
              data-testid="record-tab-data"
              onClick={() => setTab('data')}
            >
              {t('catalogue.record.tabData')}
            </button>
          </div>
        ) : null}
        <div
          className="mbody"
          id="recPanel"
          role={showData ? 'tabpanel' : undefined}
          aria-labelledby={showData ? (tab === 'data' ? 'recTabData' : 'recTabAbout') : undefined}
        >
          {showData && tab === 'data' ? (
            <DatasetRows id={record.id} />
          ) : (
            <>
              <table className="mini">
                <tbody>
                  {rows.map(([label, value]) => (
                    <tr key={label}>
                      <td style={{ width: 150, color: 'var(--ink-3)' }}>{label}</td>
                      <td>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h4 style={{ margin: '14px 0 6px', fontSize: 12.5 }}>{t('catalogue.record.versions')}</h4>
              <table className="mini">
                <thead>
                  <tr>
                    <th>{t('catalogue.record.version')}</th>
                    <th>{t('catalogue.record.date')}</th>
                    <th>{t('catalogue.record.change')}</th>
                    <th>{t('catalogue.record.by')}</th>
                  </tr>
                </thead>
                <tbody>
                  {record.versions.map((v) => (
                    <tr key={v.version}>
                      <td>{v.version}</td>
                      <td>{isoDate(v.published_at)}</td>
                      <td>{v.change_note ?? EMDASH}</td>
                      <td>{v.published_by ?? EMDASH}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h4 style={{ margin: '14px 0 6px', fontSize: 12.5 }}>{t('catalogue.record.api')}</h4>
              <pre
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                  background: '#F5F7F4',
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                  padding: '9px 11px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {record.api_snippet}
              </pre>

              <h4 style={{ margin: '14px 0 6px', fontSize: 12.5 }}>
                {t('catalogue.record.metadata')}
              </h4>
              <p style={{ margin: 0, fontSize: 12, display: 'flex', gap: 14 }}>
                <a
                  data-testid="record-meta-iso"
                  href={`/api/v1/datasets/${record.id}/metadata.xml`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('catalogue.record.iso')}
                </a>
                <a
                  data-testid="record-meta-dcat"
                  href={`/api/v1/datasets/${record.id}/metadata.jsonld`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('catalogue.record.dcat')}
                </a>
              </p>
            </>
          )}
        </div>
        <footer>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('catalogue.record.close')}
          </button>
          <button
            type="button"
            className="btn ghost"
            data-testid="record-preview"
            onClick={handlePreview}
          >
            <Icon name="map" size={14} /> {t('catalogue.record.preview')}
          </button>
          {canDownload ? (
            <a
              className="btn primary"
              data-testid="record-download"
              href={datasetDownloadUrl(record.id, record.latest_version?.version)}
            >
              <Icon name="download" size={14} /> {t('catalogue.record.download')}
            </a>
          ) : (
            <button
              type="button"
              className="btn primary"
              data-testid="record-download"
              disabled
              title={t('catalogue.record.downloadLocked')}
            >
              <Icon name="download" size={14} /> {t('catalogue.record.download')}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

export default DatasetRecord;
